import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { sucursales as apiSucursales, traspasos as apiTraspasos } from '../api/servicios'
import { cantidad as formatearCantidad, fechaHora } from '../api/formato'
import { useAuth } from '../auth/ContextoAuth'
import { BuscadorProducto } from '../componentes/BuscadorProducto'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import type { EstadoTraspaso, Sucursal, Traspaso } from '../api/tipos'
import './listados.css'

type Pestania = 'entrantes' | 'salientes' | 'todos'

const PESTANIAS: { valor: Pestania; texto: string; direccion: 'Entrantes' | 'Salientes' | 'Todos' }[] = [
    { valor: 'entrantes', texto: 'Por recibir', direccion: 'Entrantes' },
    { valor: 'salientes', texto: 'Despachados', direccion: 'Salientes' },
    { valor: 'todos', texto: 'Todos', direccion: 'Todos' },
]

const ETIQUETA_ESTADO: Record<EstadoTraspaso, string> = {
    Borrador: 'Borrador',
    EnTransito: 'En camino',
    Recibido: 'Recibido',
    Anulado: 'Anulado',
}

export function Traspasos() {
    const { puede } = useAuth()
    const [pestania, setPestania] = useState<Pestania>('entrantes')
    const [abierto, setAbierto] = useState<Traspaso | null>(null)
    const [creando, setCreando] = useState(false)

    const direccion = PESTANIAS.find((p) => p.valor === pestania)!.direccion
    const lista = usarCargar(() => apiTraspasos.listar({ direccion }), [direccion])

    const items = lista.datos?.items ?? []

    function refrescar(traspaso: Traspaso | null) {
        setAbierto(traspaso)
        setCreando(false)
        lista.recargar()
    }

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Traspasos</h1>

                {puede('traspaso.registrar') && (
                    <button
                        type="button"
                        className="boton boton-primario"
                        onClick={() => setCreando(true)}
                    >
                        Nuevo traspaso
                    </button>
                )}
            </header>

            <div className="pestanias" role="tablist">
                {PESTANIAS.map((p) => (
                    <button
                        key={p.valor}
                        type="button"
                        role="tab"
                        className="pestania"
                        aria-selected={pestania === p.valor}
                        onClick={() => setPestania(p.valor)}
                    >
                        {p.texto}
                    </button>
                ))}
            </div>

            <section className="panel columna g3">
                <Estado
                    cargando={lista.cargando}
                    error={lista.error}
                    alReintentar={lista.recargar}
                    vacio={items.length === 0}
                    mensajeVacio={
                        pestania === 'entrantes'
                            ? 'No hay nada en camino hacia este local.'
                            : 'No hay traspasos que mostrar.'
                    }
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Traspaso</th>
                                    <th className="oculto-angosto">Recorrido</th>
                                    <th className="oculto-angosto">Fecha</th>
                                    <th className="derecha">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((t) => (
                                    <tr key={t.traspasoId}>
                                        <td>
                                            <div className="columna">
                                                <span className="numero">{t.numero}</span>
                                                <span className="tenue diminuto">
                                                    {t.lineas.length} producto(s)
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto chico">
                                            {t.sucursalOrigenNombre} → {t.sucursalDestinoNombre}
                                        </td>
                                        <td className="oculto-angosto tenue chico">
                                            {fechaHora(t.enviadoEn ?? t.creadoEn)}
                                        </td>
                                        <td className="derecha">
                                            <div className="fila g2 acciones-fila">
                                                <Insignia traspaso={t} />
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() => setAbierto(t)}
                                                >
                                                    {t.estado === 'EnTransito' && !t.esOrigen
                                                        ? 'Recibir'
                                                        : t.estado === 'Borrador' && t.esOrigen
                                                          ? 'Continuar'
                                                          : 'Ver'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>
            </section>

            {creando && (
                <DialogoNuevo
                    alCreado={(t) => refrescar(t)}
                    alCerrar={() => setCreando(false)}
                />
            )}

            {abierto && (
                <DialogoTraspaso
                    traspaso={abierto}
                    alCambiar={(t) => refrescar(t)}
                    alCerrar={() => refrescar(null)}
                />
            )}
        </div>
    )
}

function Insignia({ traspaso }: { traspaso: Traspaso }) {
    const texto = ETIQUETA_ESTADO[traspaso.estado]

    if (traspaso.estado === 'Recibido' && traspaso.hayDiferencia) {
        return <span className="insignia insignia-atencion">Recibido con faltante</span>
    }

    const clase =
        traspaso.estado === 'EnTransito'
            ? 'insignia insignia-atencion'
            : traspaso.estado === 'Recibido'
              ? 'insignia insignia-ok'
              : 'insignia'

    return <span className={clase}>{texto}</span>
}

/* -------------------------------------------------------------------------- */

function DialogoNuevo({
    alCreado,
    alCerrar,
}: {
    alCreado: (traspaso: Traspaso) => void
    alCerrar: () => void
}) {
    const { usuario } = useAuth()
    const [destinos, setDestinos] = useState<Sucursal[]>([])
    const [destinoId, setDestinoId] = useState<number | null>(null)
    const [observacion, setObservacion] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    useEffect(() => {
        let vigente = true

        apiSucursales
            .listar(true)
            .then((lista) => {
                if (!vigente) return

                const otras = lista.filter((s) => s.sucursalId !== usuario?.sucursalId)

                setDestinos(otras)

                // Con un solo destino posible no hay nada que elegir.
                if (otras.length === 1) setDestinoId(otras[0]!.sucursalId)
            })
            .catch((fallo) => {
                if (vigente) setError(mensajeDeError(fallo))
            })

        return () => {
            vigente = false
        }
    }, [usuario?.sucursalId])

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (destinoId === null) {
            setError('Elija el local de destino.')
            return
        }

        setEnviando(true)

        try {
            alCreado(
                await apiTraspasos.crear({
                    sucursalDestinoId: destinoId,
                    observacion: observacion.trim() || undefined,
                }),
            )
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Nuevo traspaso"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-traspaso"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Creando…' : 'Crear'}
                    </button>
                </>
            }
        >
            <form id="forma-traspaso" className="columna g4" onSubmit={guardar}>
                {destinos.length === 0 && !error && (
                    <div className="aviso aviso-atencion">
                        No hay otro local activo al que enviar mercadería.
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="destino-traspaso">
                        Enviar a
                    </label>
                    <select
                        id="destino-traspaso"
                        className="campo"
                        value={destinoId ?? ''}
                        onChange={(e) =>
                            setDestinoId(e.target.value === '' ? null : Number(e.target.value))
                        }
                    >
                        <option value="">Elija un local</option>
                        {destinos.map((s) => (
                            <option key={s.sucursalId} value={s.sucursalId}>
                                {s.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="observacion-traspaso">
                        Observación <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="observacion-traspaso"
                        className="campo"
                        maxLength={500}
                        value={observacion}
                        onChange={(e) => setObservacion(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

/**
 * El detalle hace de las tres pantallas que el traspaso necesita, porque son la
 * misma lista vista en tres momentos: armando (se agregan y se quitan lineas),
 * en camino (el destino cuenta) y cerrado (solo se lee).
 */
function DialogoTraspaso({
    traspaso,
    alCambiar,
    alCerrar,
}: {
    traspaso: Traspaso
    alCambiar: (traspaso: Traspaso) => void
    alCerrar: () => void
}) {
    const { puede } = useAuth()
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    // Lo que el destino declara haber contado, por linea.
    const [contado, setContado] = useState<Record<number, string>>({})
    const [motivo, setMotivo] = useState('')
    const [motivoAnulacion, setMotivoAnulacion] = useState('')
    const [anulando, setAnulando] = useState(false)

    const editable = traspaso.estado === 'Borrador' && traspaso.esOrigen
    const recibible =
        traspaso.estado === 'EnTransito' && !traspaso.esOrigen && puede('traspaso.recibir')
    const anulable =
        traspaso.esOrigen &&
        (traspaso.estado === 'Borrador' || traspaso.estado === 'EnTransito') &&
        puede('traspaso.registrar')

    // Al abrir para recibir se propone lo despachado: lo habitual es que llegue
    // todo, y obligar a teclear cada cantidad invita a confirmar sin contar.
    useEffect(() => {
        if (traspaso.estado !== 'EnTransito') return

        setContado(
            Object.fromEntries(
                traspaso.lineas.map((l) => [l.traspasoDetalleId, String(l.cantidadEnviada)]),
            ),
        )
    }, [traspaso])

    async function ejecutar(accion: () => Promise<Traspaso>) {
        setError(null)
        setEnviando(true)

        try {
            alCambiar(await accion())
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    const declaradas = traspaso.lineas.map((l) => ({
        linea: l,
        valor: Number((contado[l.traspasoDetalleId] ?? '').replace(',', '.')),
    }))

    const hayInvalida = declaradas.some((d) => !Number.isFinite(d.valor) || d.valor < 0)
    const hayDiferencia = declaradas.some((d) => d.valor < d.linea.cantidadEnviada)

    function recibir() {
        if (hayInvalida) {
            setError('Hay cantidades sin declarar o mal escritas.')
            return
        }

        if (hayDiferencia && motivo.trim().length === 0) {
            setError('Llegó menos de lo despachado. Explique la diferencia antes de confirmar.')
            return
        }

        void ejecutar(() =>
            apiTraspasos.recibir(traspaso.traspasoId, {
                lineas: declaradas.map((d) => ({
                    traspasoDetalleId: d.linea.traspasoDetalleId,
                    cantidadRecibida: d.valor,
                })),
                motivoDiferencia: hayDiferencia ? motivo.trim() : undefined,
            }),
        )
    }

    return (
        <Dialogo
            titulo={`Traspaso ${traspaso.numero}`}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cerrar
                    </button>

                    {anulable && !anulando && (
                        <button
                            type="button"
                            className="boton"
                            onClick={() => setAnulando(true)}
                            disabled={enviando}
                        >
                            Anular
                        </button>
                    )}

                    {editable && (
                        <button
                            type="button"
                            className="boton boton-primario"
                            disabled={enviando || traspaso.lineas.length === 0}
                            onClick={() => void ejecutar(() => apiTraspasos.enviar(traspaso.traspasoId))}
                        >
                            {enviando ? 'Despachando…' : 'Despachar'}
                        </button>
                    )}

                    {recibible && (
                        <button
                            type="button"
                            className="boton boton-primario"
                            disabled={enviando}
                            onClick={recibir}
                        >
                            {enviando ? 'Confirmando…' : 'Confirmar recepción'}
                        </button>
                    )}
                </>
            }
        >
            <div className="columna g4">
                <div className="fila entre envuelve g3">
                    <span className="chico">
                        {traspaso.sucursalOrigenNombre} → {traspaso.sucursalDestinoNombre}
                    </span>
                    <Insignia traspaso={traspaso} />
                </div>

                {traspaso.observacion && <p className="tenue chico">{traspaso.observacion}</p>}

                {traspaso.estado === 'EnTransito' && !traspaso.esOrigen && !puede('traspaso.recibir') && (
                    <div className="aviso aviso-atencion">
                        Esta mercadería está en camino. No tiene permiso para confirmar recepciones;
                        pídaselo a quien lo tenga.
                    </div>
                )}

                {traspaso.estado === 'EnTransito' && traspaso.esOrigen && (
                    <div className="aviso aviso-atencion">
                        Ya salió de este local. El stock se descontó al despachar y lo suma el otro
                        local cuando confirme lo que llegó.
                    </div>
                )}

                {editable && (
                    <AgregarLinea
                        traspasoId={traspaso.traspasoId}
                        alAgregar={alCambiar}
                        alFallar={setError}
                    />
                )}

                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="derecha">Enviado</th>
                                {traspaso.estado !== 'Borrador' && (
                                    <th className="derecha">Recibido</th>
                                )}
                                {editable && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {traspaso.lineas.map((l) => (
                                <tr key={l.traspasoDetalleId}>
                                    <td>
                                        <div className="columna">
                                            <span>{l.productoNombre}</span>
                                            <span className="tenue diminuto numero">{l.sku}</span>
                                        </div>
                                    </td>
                                    <td className="derecha numero">
                                        {formatearCantidad(l.cantidadEnviada, 3)}
                                    </td>

                                    {traspaso.estado !== 'Borrador' && (
                                        <td className="derecha">
                                            {recibible ? (
                                                <input
                                                    className="campo numero derecha"
                                                    inputMode="decimal"
                                                    style={{ maxWidth: '7rem' }}
                                                    value={contado[l.traspasoDetalleId] ?? ''}
                                                    onChange={(e) =>
                                                        setContado((actual) => ({
                                                            ...actual,
                                                            [l.traspasoDetalleId]: e.target.value,
                                                        }))
                                                    }
                                                />
                                            ) : (
                                                l.cantidadRecibida === null ? (
                                                    <span className="numero tenue">—</span>
                                                ) : l.cantidadRecibida < l.cantidadEnviada ? (
                                                    <span className="insignia insignia-atencion numero">
                                                        {formatearCantidad(l.cantidadRecibida, 3)}
                                                    </span>
                                                ) : (
                                                    <span className="numero">
                                                        {formatearCantidad(l.cantidadRecibida, 3)}
                                                    </span>
                                                )
                                            )}
                                        </td>
                                    )}

                                    {editable && (
                                        <td className="derecha">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma"
                                                disabled={enviando}
                                                onClick={() =>
                                                    void ejecutar(() =>
                                                        apiTraspasos.quitarLinea(
                                                            traspaso.traspasoId,
                                                            l.traspasoDetalleId,
                                                        ),
                                                    )
                                                }
                                            >
                                                Quitar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {traspaso.lineas.length === 0 && (
                    <p className="tenue chico">Todavía no hay productos en este traspaso.</p>
                )}

                {recibible && hayDiferencia && (
                    <div>
                        <label className="etiqueta" htmlFor="motivo-diferencia">
                            Qué pasó con lo que falta
                        </label>
                        <input
                            id="motivo-diferencia"
                            className="campo"
                            maxLength={500}
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Lo que no se recibe no vuelve al otro local: se da por perdido en el
                            traslado y queda anotado con su explicación.
                        </p>
                    </div>
                )}

                {traspaso.motivoDiferencia && (
                    <div className="aviso aviso-atencion">
                        Diferencia declarada: {traspaso.motivoDiferencia}
                    </div>
                )}

                {anulando && (
                    <div className="columna g2">
                        <label className="etiqueta" htmlFor="motivo-anulacion">
                            Motivo de la anulación
                        </label>
                        <input
                            id="motivo-anulacion"
                            className="campo"
                            maxLength={500}
                            value={motivoAnulacion}
                            onChange={(e) => setMotivoAnulacion(e.target.value)}
                        />
                        {traspaso.estado === 'EnTransito' && (
                            <p className="tenue diminuto">
                                La mercadería vuelve al inventario de este local.
                            </p>
                        )}
                        <div className="fila g2">
                            <button
                                type="button"
                                className="boton"
                                onClick={() => setAnulando(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="boton boton-peligro"
                                disabled={enviando || motivoAnulacion.trim().length === 0}
                                onClick={() =>
                                    void ejecutar(() =>
                                        apiTraspasos.anular(
                                            traspaso.traspasoId,
                                            motivoAnulacion.trim(),
                                        ),
                                    )
                                }
                            >
                                Confirmar anulación
                            </button>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

function AgregarLinea({
    traspasoId,
    alAgregar,
    alFallar,
}: {
    traspasoId: number
    alAgregar: (traspaso: Traspaso) => void
    alFallar: (mensaje: string | null) => void
}) {
    const [elegido, setElegido] = useState<{ productoId: number; nombre: string } | null>(null)
    const [cantidad, setCantidad] = useState('')
    const [enviando, setEnviando] = useState(false)

    /*
     * Sin <form> a proposito. El buscador acepta la pistola de codigo de
     * barras, y el lector termina cada lectura con Enter: dentro de un
     * formulario ese Enter lo enviaria antes de que la busqueda resuelva, y el
     * cajero veria "elija un producto" en vez del producto que acaba de
     * escanear.
     */
    async function agregar() {
        alFallar(null)

        const valor = Number(cantidad.replace(',', '.'))

        if (elegido === null) {
            alFallar('Elija un producto.')
            return
        }

        if (!Number.isFinite(valor) || valor <= 0) {
            alFallar('La cantidad debe ser mayor que cero.')
            return
        }

        setEnviando(true)

        try {
            alAgregar(
                await apiTraspasos.agregarLinea(traspasoId, {
                    productoId: elegido.productoId,
                    cantidad: valor,
                }),
            )
            setElegido(null)
            setCantidad('')
        } catch (fallo) {
            alFallar(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <div className="columna g3">
            <BuscadorProducto
                etiqueta="Agregar producto"
                alElegir={(p) => setElegido({ productoId: p.productoId, nombre: p.nombre })}
            />

            {elegido && (
                <div className="fila envuelve g2">
                    <span className="crece chico">{elegido.nombre}</span>

                    <div style={{ maxWidth: '8rem' }}>
                        <label className="etiqueta" htmlFor="cantidad-traspaso">
                            Cantidad
                        </label>
                        <input
                            id="cantidad-traspaso"
                            className="campo numero derecha"
                            inputMode="decimal"
                            autoFocus
                            value={cantidad}
                            onChange={(e) => setCantidad(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    void agregar()
                                }
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        className="boton boton-primario"
                        disabled={enviando}
                        onClick={() => void agregar()}
                    >
                        Agregar
                    </button>
                </div>
            )}
        </div>
    )
}
